import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactNode, createContext, useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { IntroducingBrokerCampaignResultDto } from '@model/IntroducingBrokerCampaignResultDto'

import { useGuardedContext } from '@global/useGuardedContext'

import { useAccountReadContext } from './AccountContextContext'

interface IBCampaignsResultsReadContextProps {
  ibCampaignsResults: IntroducingBrokerCampaignResultDto[]
}

interface IBCampaignsResultsWriteContextProps {
  refreshIBCampaigns: (campaigns?: IntroducingBrokerCampaignResultDto[]) => void
}

const IBCampaignsResultsReadContext = createContext<IBCampaignsResultsReadContextProps | undefined>(
  undefined
)

IBCampaignsResultsReadContext.displayName = 'IBCampaignsResultsReadContext'

const IBCampaignsResultsWriteContext = createContext<
  IBCampaignsResultsWriteContextProps | undefined
>(undefined)

export function useIBCampaignsResultsReadContext(): IBCampaignsResultsReadContextProps {
  return useGuardedContext(IBCampaignsResultsReadContext)
}

export function useIBCampaignsResultsWriteContext() {
  return useGuardedContext(IBCampaignsResultsWriteContext)
}

interface Props {
  children: ReactNode
}

export function IBCampaignsResultsContextProvider({ children }: Props) {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const isIB = !!account?.clientIntroducingBroker?.id

  const { data: ibCampaignsResults = [] } = useQuery({
    queryKey: queryKeys.ib.campaigns(),
    queryFn: () => apiClient.getIBCampaignsResults().then((r) => r.items),
    enabled: isIB,
  })

  const writeContext = useMemo<IBCampaignsResultsWriteContextProps>(() => {
    return {
      async refreshIBCampaigns(campaigns?: IntroducingBrokerCampaignResultDto[]) {
        if (campaigns?.length) {
          queryClient.setQueryData(queryKeys.ib.campaigns(), campaigns)
        } else if (campaigns === undefined) {
          queryClient.setQueryData(queryKeys.ib.campaigns(), [])
        } else {
          queryClient.invalidateQueries({ queryKey: queryKeys.ib.campaigns() })
        }
      },
    }
  }, [queryClient])

  const readContext = useMemo<IBCampaignsResultsReadContextProps>(() => {
    return { ibCampaignsResults }
  }, [ibCampaignsResults])

  return (
    <IBCampaignsResultsReadContext.Provider value={readContext}>
      <IBCampaignsResultsWriteContext.Provider value={writeContext}>
        {children}
      </IBCampaignsResultsWriteContext.Provider>
    </IBCampaignsResultsReadContext.Provider>
  )
}
